/**
 * StreamingBuffer - Smooths out jerky streaming by buffering tokens and releasing them at a steady rate
 */

class StreamingBuffer {
  constructor(options = {}) {
    // Configuration
    this.charsPerRelease = options.charsPerRelease || 4; // Characters to release each interval
    this.releaseInterval = options.releaseInterval || 40; // Milliseconds between releases
    this.flushDelay = options.flushDelay || 100; // Delay before flushing on complete

    // State
    this.buffer = '';
    this.isStreaming = false;
    this.intervalId = null;
    this.onRelease = null;
    this.messageId = null;
    this.isPaused = false;
    this.flushTimeoutId = null;
  }

  /**
   * Start buffering for a new message
   * @param {number} messageId - ID of the message being streamed
   * @param {function} onRelease - Callback function to handle released text
   */
  start(messageId, onRelease) {
    this.stop(); // Clean up any existing streaming

    this.messageId = messageId;
    this.onRelease = onRelease;
    this.buffer = '';
    this.isStreaming = true;
    this.isPaused = false;

    // Start the release interval
    this.intervalId = setInterval(() => {
      this.releaseChars();
    }, this.releaseInterval);
  }

  /**
   * Add text to the buffer
   * @param {string} text - Text to add to buffer
   */
  addToBuffer(text) {
    if (!this.isStreaming || !text) return;

    this.buffer += text;
  }

  /**
   * Release up to charsPerRelease characters per tick for a smooth typewriter effect.
   * If the buffer backlog grows large (model bursting tokens), catch up faster
   * to avoid visual lag without sacrificing smoothness during normal flow.
   */
  releaseChars() {
    if (!this.isStreaming || this.isPaused || !this.buffer || !this.onRelease) return;

    const backlog = this.buffer.length;
    // Graduated catch-up: the larger the backlog, the faster we release.
    // Thresholds are kept low so catch-up engages quickly — with a base
    // rate of ~3 chars / 30ms the buffer rarely grows large before draining,
    // so high thresholds would never trigger.
    let count;
    if (backlog > 80) {
      // Large backlog — dump it all so the user isn't waiting
      count = backlog;
    } else if (backlog > 30) {
      // Moderate backlog — 6x base speed
      count = Math.min(backlog, this.charsPerRelease * 6);
    } else if (backlog > 10) {
      // Small backlog building up — 3x base speed
      count = Math.min(backlog, this.charsPerRelease * 3);
    } else {
      // Normal flow — smooth typewriter drip
      count = this.charsPerRelease;
    }

    const released = this.buffer.slice(0, count);
    this.buffer = this.buffer.slice(count);
    this.onRelease(this.messageId, released);
  }

  /**
   * Pause the buffer (useful during tool execution status messages)
   */
  pause() {
    this.isPaused = true;
  }

  /**
   * Resume the buffer after pausing
   */
  resume() {
    this.isPaused = false;
  }

  /**
   * Immediately release extra content plus all buffered content.
   * Use at hard stream boundaries (tool call start, stream end) to
   * ensure nothing is held back waiting for the timed drip.
   * @param {string} extra - Additional content to prepend before flushing buffer
   */
  flushImmediate(extra = '') {
    if (!this.onRelease) return;
    const toRelease = this.buffer + extra;
    if (toRelease) {
      this.onRelease(this.messageId, toRelease);
      this.buffer = '';
    }
  }

  /**
   * Flush all remaining buffer content immediately
   */
  flush() {
    if (this.buffer && this.onRelease) {
      this.onRelease(this.messageId, this.buffer);
      this.buffer = '';
    }
  }

  /**
   * Mark streaming as complete and schedule flush
   */
  complete() {
    if (!this.isStreaming) return;

    // Clear any existing flush timeout
    if (this.flushTimeoutId) {
      clearTimeout(this.flushTimeoutId);
    }

    // Schedule flush after a small delay to allow final tokens to arrive
    this.flushTimeoutId = setTimeout(() => {
      this.flush();
      this.stop();
    }, this.flushDelay);
  }

  /**
   * Stop streaming and clean up
   */
  stop() {
    this.isStreaming = false;

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    if (this.flushTimeoutId) {
      clearTimeout(this.flushTimeoutId);
      this.flushTimeoutId = null;
    }

    // Final flush
    this.flush();

    // Clear state
    this.buffer = '';
    this.messageId = null;
    this.onRelease = null;
    this.isPaused = false;
  }

  /**
   * Check if currently streaming a specific message
   */
  isStreamingMessage(messageId) {
    return this.isStreaming && this.messageId === messageId;
  }

  /**
   * Adjust streaming speed dynamically
   * @param {number} charsPerRelease - New characters per release
   * @param {number} releaseInterval - New release interval in ms
   */
  adjustSpeed(charsPerRelease, releaseInterval) {
    if (charsPerRelease !== undefined) {
      this.charsPerRelease = charsPerRelease;
    }

    if (releaseInterval !== undefined && releaseInterval !== this.releaseInterval) {
      this.releaseInterval = releaseInterval;

      // Restart interval with new timing if currently streaming
      if (this.isStreaming && this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = setInterval(() => {
          this.releaseChars();
        }, this.releaseInterval);
      }
    }
  }
}

export default StreamingBuffer;